// Simple CRM — curated client/contact context the AI uses when summarising.
// Entries match by email or domain; the notes get injected into prompts.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, Mail, Pencil, Plus, Save, Trash2, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MenuPicker } from '@/components/ui/menu-picker';

interface CrmEntry {
  id: number;
  matchType: 'email' | 'domain';
  matchValue: string;
  name: string;
  company: string;
  notes: string;
  updatedAt: string;
}

function useCrm() {
  return useQuery<{ entries: CrmEntry[] }>({
    queryKey: ['crm'],
    queryFn: async () => {
      const r = await fetch('/api/crm', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });
}

export function CrmSettings() {
  const crmQ = useCrm();
  const [adding, setAdding] = useState(false);

  if (crmQ.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading CRM…
        </CardContent>
      </Card>
    );
  }

  const entries = crmQ.data?.entries ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Building2 className="size-3.5" strokeWidth={2} />
          Client CRM
        </CardTitle>
        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAdding(true)} disabled={adding}>
          <Plus className="size-3.5" strokeWidth={2} />
          Add entry
        </Button>
      </CardHeader>
      <CardContent className="space-y-1 pt-0">
        <p className="pb-2 text-xs text-muted-foreground">
          Notes you add here are fed to the AI when it summarises a thread with a matching
          sender or domain — so summaries know the client&rsquo;s context.
        </p>
        {entries.length === 0 && !adding && (
          <div className="flex flex-col items-center gap-2 rounded-md border border-dashed py-8 text-center">
            <Users className="size-6 text-muted-foreground/50" strokeWidth={1.5} />
            <p className="text-xs text-muted-foreground">
              No client notes yet. Add one keyed to an email or a whole domain.
            </p>
          </div>
        )}
        {adding && <CrmEditor initial={null} onClose={() => setAdding(false)} />}
        {entries.map((e, i) => (
          <div key={e.id}>
            {i > 0 && <Separator />}
            <CrmRow entry={e} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function CrmRow({ entry }: { entry: CrmEntry }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const del = useMutation({
    mutationFn: async () => {
      const r = await fetch(`/api/crm/${entry.id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm'] }),
  });

  if (editing) return <CrmEditor initial={entry} onClose={() => setEditing(false)} />;

  const Icon = entry.matchType === 'domain' ? Building2 : Mail;
  return (
    <div className="flex items-start gap-3 py-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">
            {entry.company || entry.name || entry.matchValue}
          </span>
          <Badge variant="outline" className="h-4 px-1.5 text-[9px]">
            {entry.matchType}: {entry.matchValue}
          </Badge>
        </div>
        {entry.name && entry.company && (
          <div className="text-xs text-muted-foreground">{entry.name}</div>
        )}
        {entry.notes && (
          <div className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">{entry.notes}</div>
        )}
      </div>
      <div className="flex shrink-0 gap-1">
        <Button variant="ghost" size="icon" className="size-7" onClick={() => setEditing(true)} aria-label="Edit">
          <Pencil className="size-3.5" strokeWidth={1.75} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground hover:text-destructive"
          onClick={() => {
            if (confirm('Delete this CRM entry?')) del.mutate();
          }}
          disabled={del.isPending}
          aria-label="Delete"
        >
          <Trash2 className="size-3.5" strokeWidth={1.75} />
        </Button>
      </div>
    </div>
  );
}

function CrmEditor({ initial, onClose }: { initial: CrmEntry | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [matchType, setMatchType] = useState<'email' | 'domain'>(initial?.matchType ?? 'domain');
  const [matchValue, setMatchValue] = useState(initial?.matchValue ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [company, setCompany] = useState(initial?.company ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const url = initial ? `/api/crm/${initial.id}` : '/api/crm';
      const body = initial
        ? { name, company, notes }
        : { matchType, matchValue, name, company, notes };
      const r = await fetch(url, {
        method: initial ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      toast.success(initial ? 'Entry updated' : 'Entry added');
      qc.invalidateQueries({ queryKey: ['crm'] });
      onClose();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
  });

  return (
    <div className="space-y-2 rounded-md border bg-card/50 p-3">
      {!initial && (
        <div className="flex gap-2">
          <MenuPicker
            className="w-32"
            value={matchType}
            onChange={(v) => setMatchType(v as 'email' | 'domain')}
            options={[
              { value: 'domain', label: 'Domain' },
              { value: 'email', label: 'Email' },
            ]}
          />
          <input
            type="text"
            value={matchValue}
            onChange={(e) => setMatchValue(e.target.value)}
            placeholder={matchType === 'domain' ? 'acme.com' : 'jane@acme.com'}
            className="h-8 flex-1 rounded-md border bg-background px-2.5 text-sm outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company"
          className="rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Contact name (optional)"
          className="rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
        />
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        placeholder="Notes the AI should know — plan, history, preferences, gotchas…"
        className="block w-full resize-y rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
      />
      <div className="flex justify-end gap-1">
        <Button size="xs" variant="ghost" onClick={onClose} disabled={save.isPending}>
          <X className="size-3" strokeWidth={2.5} />
          Cancel
        </Button>
        <Button
          size="xs"
          onClick={() => save.mutate()}
          disabled={save.isPending || (!initial && !matchValue.trim())}
          className="gap-1"
        >
          {save.isPending ? <Loader2 className="size-3 animate-spin" /> : <Save className="size-3" strokeWidth={2} />}
          Save
        </Button>
      </div>
    </div>
  );
}
